import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { resetPassword } from '../api/auth';
import BrandLogo from '../components/BrandLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const inputClass =
  'flex h-11 w-full rounded-lg border border-input bg-card px-4 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';
  const isWelcome = searchParams.get('welcome') === '1';
  const isAdminAccount = searchParams.get('admin') === '1';

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isInvalidLink = !email || !token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await resetPassword(email, token, password);
      if (result.error) setError(result.error);
      else if (isAdminAccount) {
        // /admin is a separate Filament panel, not part of this SPA's router.
        window.location.href = '/admin/login';
      } else {
        navigate('/login', { state: { passwordReset: true } });
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-muted/50 p-6 pt-safe pb-safe">
      <Card className="w-full max-w-sm shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <BrandLogo className="mb-6" width={160} />

          {isInvalidLink ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-destructive">This link is invalid or has expired.</p>
              <Link to="/forgot-password" className="block text-sm font-medium text-primary hover:underline">
                Request a new link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {isWelcome ? (
                <div>
                  <h1 className="text-base font-semibold text-foreground">Welcome — set up your account</h1>
                  <p className="mt-1 text-sm text-muted-foreground">Create a password to activate your account. Minimum 12 characters.</p>
                </div>
              ) : (
                <div>
                  <h1 className="text-base font-semibold text-foreground">Set new password</h1>
                  <p className="mt-1 text-sm text-muted-foreground">Choose a password of at least 12 characters.</p>
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="mb-1 block text-sm font-medium text-foreground">
                  {isWelcome ? 'Create password' : 'New password'}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  required
                  minLength={12}
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full" size="lg">
                {loading ? 'Saving…' : isWelcome ? 'Activate my account' : 'Set new password'}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
